import { StoryData } from "@app/types/ServerCustomContext";
import AdSettings from "@app/utils/AdSettings";
import AnalyticsGrapeshot from "@app/utils/trackers/grapeshot";
import MetaData from "@components/Modules/MetaData";
import { mapStoryDataToMetaData } from "@components/Modules/MetaData/mappers";
import PubBaseLayout from "@pub/BaseLayout";
import { maybeScrollToId, UserSubmissionBox } from "@sphtech/dmg-design-system";
import { NoSSR } from "@sphtech/web2-core/components";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import InfiniteScroll from "./InfiniteScroll";
import SinglePostLayout from "./SinglePostLayout";

export type ArticleLayoutProps = {
  storyData: StoryData;
  userSubmissionBox?: UserSubmissionBox;
};

/**
 * ArticleLayout
 * Description: [Description of what the component does]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function ArticleLayout(props: ArticleLayoutProps): ReactNode {
  const { storyData, userSubmissionBox } = props;
  const { typeName, sections, processedElements } = storyData;
  const { headline } = processedElements;
  const location = useLocation();

  const [currentInViewStory, setCurrentInViewStory] = useState(storyData);
  const [currentInViewHTML, setCurrentInViewHTML] = useState<HTMLElement>();

  const pathRef = useRef(storyData.path);
  useEffect(() => {
    const { path } = currentInViewStory;
    if (pathRef.current !== path) {
      pathRef.current = path;
      history.pushState({}, "", path);
      window.SPHMGTM.cmd.push(`pageview=${path}`);
    }
  }, [currentInViewStory]);

  useEffect(() => {
    // ensure once that location hash is scrolled instanly into center if found as id selector
    // - this currently does not work very well if lazy SWR loaded elements like embedded galleries and grouped relations are before the target hash
    maybeScrollToId(window.location.hash.slice(1), "instant");
  }, []);

  const inViewList = useRef<StoryData[]>([]);

  const onInView = useCallback(
    (
      storyData: StoryData,
      inView: boolean,
      articleElement: HTMLElement,
      hide: boolean,
    ) => {
      const initiallyInView = inViewList.current[0];
      const inViewListIndex = inViewList.current.findIndex(
        (d) => d.path === storyData.path,
      );

      if (inView && inViewListIndex === -1) {
        inViewList.current.unshift(storyData);
      } else if (!inView && inViewListIndex !== -1) {
        inViewList.current.splice(inViewListIndex, 1);
      }

      const newInView = inViewList.current.length && inViewList.current[0];

      if (!hide) {
        setCurrentInViewHTML(articleElement);
      }

      if (newInView && initiallyInView !== newInView) {
        setCurrentInViewStory(newInView);
        if (!hide) {
          setCurrentInViewHTML(articleElement);
        } else {
          setCurrentInViewHTML(undefined);
        }
      }
    },
    [],
  );

  const showLeaderboard = typeName !== "FeatureStory";
  return (
    <PubBaseLayout
      ArticleTitle={currentInViewStory.processedElements.headline}
      section={currentInViewStory.sections[0]}
      leaderboard={showLeaderboard}
      currentInViewArticleRef={currentInViewHTML}
    >
      <MetaData {...mapStoryDataToMetaData(currentInViewStory)} />
      <AnalyticsGrapeshot />
      <AdSettings
        StoryData={storyData}
        pageTitle={headline || ""}
        pageType="article"
        sectionName={sections[0]?.name}
        currentLocation={location}
        adUnits="lb1,imu1,imu2,imu3,midarticlespecial,midarticlespecial2,catfish,prestitial"
      />
      <SinglePostLayout
        storyData={storyData}
        userSubmissionBox={userSubmissionBox}
        onInView={onInView}
        hideContent={false}
      />
      {typeName !== "FeatureStory" && (
        <NoSSR>
          <InfiniteScroll storyData={storyData} onInView={onInView} />
        </NoSSR>
      )}
    </PubBaseLayout>
  );
}
